import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/AuthProvider";
import { Upload, Trash2, Image as ImageIcon, ChevronUp, ChevronDown } from "lucide-react";

interface HeroBanner {
  id: string;
  image_url: string;
  title: string | null;
  subtitle: string | null;
  sort_order: number;
  is_active: boolean;
}

export function HeroBannerManager() {
  const [banners, setBanners] = useState<HeroBanner[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const { toast } = useToast();
  const { hasPermission } = useAuth();

  useEffect(() => {
    fetchBanners();
  }, []);

  const fetchBanners = async () => {
    try {
      const { data, error } = await supabase
        .from('hero_banners')
        .select('*')
        .order('sort_order', { ascending: true });

      if (error) throw error;
      setBanners(data || []);
    } catch (error) {
      console.error('Error fetching banners:', error);
      toast({
        title: "Error",
        description: "Failed to fetch hero banners",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      // Get photographer profile
      const { data: profile } = await supabase
        .from('photographer_profiles')
        .select('id')
        .limit(1)
        .single();

      if (!profile) throw new Error('Profile not found');

      const fileExt = file.name.split('.').pop();
      const fileName = `hero/${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('profile')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('profile')
        .getPublicUrl(fileName);

      // Create new banner
      const { error } = await supabase
        .from('hero_banners')
        .insert([{
          photographer_id: profile.id,
          image_url: publicUrl,
          sort_order: banners.length,
          is_active: true
        }]);

      if (error) throw error;
      toast({ title: "Hero banner added successfully!" });
      fetchBanners();
    } catch (error) {
      console.error('Error uploading banner:', error);
      toast({
        title: "Error",
        description: "Failed to upload banner",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this banner?')) return;

    try {
      const { error } = await supabase
        .from('hero_banners')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast({ title: "Banner deleted successfully!" });
      fetchBanners();
    } catch (error) {
      console.error('Error deleting banner:', error);
      toast({
        title: "Error",
        description: "Failed to delete banner",
        variant: "destructive",
      });
    }
  };

  const handleMoveUp = async (index: number) => {
    if (index === 0) return;
    const current = banners[index];
    const prev = banners[index - 1];

    // Optimistic update
    const newBanners = [...banners];
    newBanners[index] = prev;
    newBanners[index - 1] = current;
    setBanners(newBanners);

    try {
      // Swap sort_order in DB
      const { error: error1 } = await supabase
        .from('hero_banners')
        .update({ sort_order: prev.sort_order })
        .eq('id', current.id);

      const { error: error2 } = await supabase
        .from('hero_banners')
        .update({ sort_order: current.sort_order })
        .eq('id', prev.id);

      if (error1 || error2) throw error1 || error2;

      // Ideally re-fetch to ensure sync, but optimistic is fine for now if no error
    } catch (error) {
      console.error('Error reordering banners:', error);
      toast({
        title: "Error",
        description: "Failed to reorder banners",
        variant: "destructive",
      });
      fetchBanners(); // Revert on error
    }
  };

  const handleMoveDown = async (index: number) => {
    if (index === banners.length - 1) return;
    const current = banners[index];
    const next = banners[index + 1];

    // Optimistic update
    const newBanners = [...banners];
    newBanners[index] = next;
    newBanners[index + 1] = current;
    setBanners(newBanners);

    try {
      // Swap sort_order in DB
      const { error: error1 } = await supabase
        .from('hero_banners')
        .update({ sort_order: next.sort_order })
        .eq('id', current.id);

      const { error: error2 } = await supabase
        .from('hero_banners')
        .update({ sort_order: current.sort_order })
        .eq('id', next.id);

      if (error1 || error2) throw error1 || error2;
    } catch (error) {
      console.error('Error reordering banners:', error);
      toast({
        title: "Error",
        description: "Failed to reorder banners",
        variant: "destructive",
      });
      fetchBanners(); // Revert on error
    }
  };

  if (loading) {
    return <div className="flex justify-center py-8">Loading banners...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Hero Banner Slideshow</h2>
          <p className="text-muted-foreground">Manage homepage background images</p>
        </div>
        <Button onClick={() => document.getElementById('banner-upload')?.click()} disabled={uploading}>
          <Upload className="h-4 w-4 mr-2" />
          {uploading ? 'Uploading...' : 'Add Banner'}
        </Button>
        <input
          id="banner-upload"
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleImageUpload}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {banners.map((banner, index) => (
          <Card key={banner.id}>
            <CardContent className="p-4">
              <div className="relative aspect-video rounded-lg overflow-hidden bg-muted mb-3 group">
                <img
                  src={banner.image_url}
                  alt="Hero banner"
                  className="w-full h-full object-cover"
                />

                {/* Overlay actions ?? Maybe buttons below is cleaner as per user request 'like services' */}
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">
                  Sequence: {index + 1}
                </span>

                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={() => handleMoveUp(index)}
                    disabled={index === 0}
                  >
                    <ChevronUp className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={() => handleMoveDown(index)}
                    disabled={index === banners.length - 1}
                  >
                    <ChevronDown className="h-4 w-4" />
                  </Button>

                  <div className="w-px h-4 bg-border mx-1" />
                  {hasPermission('delete_hero') && (
                    <Button
                      variant="destructive"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => handleDelete(banner.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {banners.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <ImageIcon className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground text-center">
              No hero banners yet. Add your first banner to create a slideshow.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}